<template>
    <div class="relative">
        <!-- 虚化背景层 (仅在浅色主题显示) -->
        <div v-if="albumImagePreview && isLightTheme" class="fixed inset-0 -z-10 overflow-hidden">
            <div class="absolute inset-0 bg-cover bg-center blur-3xl opacity-15 scale-110 transition-all duration-500"
                :style="{ backgroundImage: `url(${albumImagePreview})` }">
            </div>
            <div class="absolute inset-0 bg-gradient-to-b from-base-100/70 via-base-100/50 to-base-100/70"></div>
        </div>

        <!-- 操作头部 -->
        <div class="flex items-center justify-between">
            <button @click="handleClick" class="btn btn-sm btn-neutral flex-1 mr-2" v-if="mode !== 'form'">
                保存信息
            </button>
            <button class="btn btn-sm btn-ghost tooltip tooltip-bottom" data-tip="版本历史" @click="showVersionHistory"
                v-if="mode !== 'form'">
                <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"
                        fill="none" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>
            <button class="btn btn-sm btn-ghost" @click="openSettingsModal" v-if="mode !== 'form'">
                <svg-icon name="settings" class="w-4 h-4 fill-current" />
            </button>

            <!-- 配置模态框 -->
            <dialog ref="settingsModal" class="modal modal-bottom md:modal-middle">
                <div class="modal-box max-w-4xl w-11/12 md:w-5/6 lg:w-4/5 p-0 flex flex-col max-h-[90vh]">
                    <div class="p-6 border-b border-base-200">
                        <h3 class="text-lg font-bold">刮削配置</h3>
                    </div>
                    <div class="flex-1 overflow-y-auto p-6">
                        <scraping-settings-content @config-updated="handleConfigUpdated" @close="closeSettingsModal" />
                    </div>
                </div>
                <div class="modal-backdrop">
                    <form method="dialog" class="modal-backdrop">
                        <button>close</button>
                    </form>
                </div>
            </dialog>
        </div>

        <!-- 加载状态 -->
        <div class="flex justify-center" v-if="loading">
            <span class="loading loading-infinity loading-md text-primary"></span>
        </div>

        <!-- 动态分组渲染 -->
        <template v-for="group in fieldGroups">
            <div class="space-y-4 mt-6" v-if="hasGroupFields(group.id)" :key="group.id">
                <div class="flex items-center space-x-3">
                    <div class="w-1 h-5" :class="getGroupInfo(group.id).barClass"></div>
                    <h3 class="text-lg font-semibold text-base-content">{{ getGroupInfo(group.id).title }}</h3>
                    <div class="badge badge-sm" :class="getGroupInfo(group.id).badgeClass">{{
                        getGroupInfo(group.id).badge }}</div>
                </div>

                <div class="ml-4 pl-3 border-l border-base-200 space-y-3">
                    <!-- 基本信息组的特殊字段：标题 -->
                    <div class="flex items-center space-x-3" v-if="group.id === 'basic'">
                        <label class="label-text font-medium min-w-20">标题</label>
                        <div class="flex-1">
                            <single-input v-model="musicInfo.title" :music-data="musicInfo" field-name="title" :placeholder="'请输入标题'"></single-input>
                        </div>
                        <button class="btn btn-sm btn-ghost btn-circle" @click="toggleLock('title')"
                            v-if="mode !== 'form'">
                            <svg class="w-4 h-4 fill-current" viewBox="0 0 1024 1024">
                                <path
                                    d="M244.072422 374.434133c-5.005681 11.805512-19.752318 17.979953-32.430556 12.403989-12.378359-4.733995-18.282396-19.479351-12.943516-32.152463 7.048449-17.981235 16.486964-34.192668 26.226639-49.480114a378.437652 378.437652 0 0 1 35.940684-43.332585 280.427042 280.427042 0 0 1 43.910558-35.961188 275.658446 275.658446 0 0 1 49.544191-27.11987c12.678238-5.331191 27.723474 0.598477 32.429274 13.545838 5.60544 11.805512-0.598477 27.121151-13.276715 32.12555a264.739757 264.739757 0 0 0-40.973277 22.115471 333.234024 333.234024 0 0 0-36.267476 30.030239 267.88721 267.88721 0 0 0-30.062277 36.559666 275.207345 275.207345 0 0 0-22.092403 41.265467z m572.426356 383.462556l178.937061 179.45352a41.00916 41.00916 0 0 1 0.299879 58.950667 42.003632 42.003632 0 0 1-58.657195 0L757.541703 816.516719a444.40473 444.40473 0 0 1-116.117436 70.452455 450.588143 450.588143 0 0 1-172.734425 34.192669 455.128625 455.128625 0 0 1-173.632782-34.192669 456.816409 456.816409 0 0 1-244.940022-245.525684l-1.170043-2.040205a451.589022 451.589022 0 0 1-33.029033-170.933867 443.242377 443.242377 0 0 1 34.199076-172.399945A453.776605 453.776605 0 0 1 468.689842 15.778274 454.901794 454.901794 0 0 1 641.126951 50.242628l0.298598 0.298598v-0.298598a463.787966 463.787966 0 0 1 147.104982 98.450178l1.740326 2.040205a454.945366 454.945366 0 0 1 96.398438 144.707228l0.897075 2.365716a446.939609 446.939609 0 0 1-0.897075 343.640098 465.801259 465.801259 0 0 1-70.165391 116.450636zM610.163754 126.870806A371.431494 371.431494 0 0 0 126.461996 326.994224c-18.01071 43.604271-27.424876 91.373534-27.424875 141.475194a378.76957 378.76957 0 0 0 26.5278 139.980922l0.897075 1.467359a380.959715 380.959715 0 0 0 80.449719 120.259361 368.02517 368.02517 0 0 0 120.281147 80.163936c43.03655 17.950478 90.784027 27.690154 141.49698 27.690154a365.467224 365.467224 0 0 0 260.585016-107.282525l0.570284-0.571565a374.376464 374.376464 0 0 0 80.204946-120.259361c18.256765-43.032705 27.968247-91.044179 27.968246-141.449563a368.039267 368.039267 0 0 0-27.096802-139.13639l-0.871444-2.040205c-18.609188-44.22966-44.835827-84.59677-78.136547-118.190962l-2.068399-1.76852c-34.197795-34.165756-75.171071-61.884104-119.681388-79.864057v-0.597196z m101.100394 341.598612a25.41799 25.41799 0 0 1 25.056596-25.052752 25.007898 25.007898 0 0 1 25.057879 25.052752 297.604754 297.604754 0 0 1-22.391002 112.561172l-0.299879 0.598478a292.432473 292.432473 0 0 1-157.987788 157.38931 291.206043 291.206043 0 0 1-112.006267 22.658843 25.039937 25.039937 0 1 1 0-50.078592c31.525792 0 63.064398-6.499952 93.127957-18.278552a241.078753 241.078753 0 0 0 78.430018-52.744187 237.107272 237.107272 0 0 0 52.158525-77.796939l0.871444-1.170042a246.677785 246.677785 0 0 0 17.983798-93.139491z">
                                </path>
                            </svg>
                        </button>
                    </div>

                    <!-- 动态字段渲染 -->
                    <template v-for="(item, index) in getGroupFields(group.id)">
                        <!-- 文件名 -->
                        <div :key="group.id + '_' + index" v-if="false"></div>
                        <div class="flex items-center space-x-3" v-if="item === 'filename'">
                            <label class="label-text font-medium min-w-20">文件名</label>
                            <div class="flex-1">
                                <single-input v-model="musicInfo.filename" :music-data="musicInfo" field-name="filename" :placeholder="'输入$查看支持的变量'"></single-input>
                            </div>
                        </div>

                        <!-- 艺术家 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'artist'">
                            <label class="label-text font-medium min-w-20">艺术家</label>
                            <div class="flex-1">
                                <single-tag v-model="musicInfo.artist_list" :options="musicInfo.artist_list_regex"
                                    @change="handleArtistChange"></single-tag>
                            </div>
                        </div>

                        <!-- 专辑 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'album'">
                            <label class="label-text font-medium min-w-20">专辑</label>
                            <div class="flex-1">
                                <single-input v-model="musicInfo.album" :music-data="musicInfo" field-name="album"></single-input>
                            </div>
                        </div>

                        <!-- 专辑艺术家 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'albumartist'">
                            <label class="label-text font-medium min-w-20">专辑艺术家</label>
                            <div class="flex-1 max-w-xs">
                                <single-select :options="albumArtistList" v-model="musicInfo.albumartist" is-var>
                                </single-select>
                            </div>
                        </div>

                        <!-- 年份 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'year'">
                            <label class="label-text font-medium min-w-20">年份</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.year" :music-data="musicInfo" field-name="year"></single-input>
                            </div>
                        </div>

                        <!-- 风格 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'genre'">
                            <label class="label-text font-medium min-w-20">风格</label>
                            <div class="flex-1 max-w-xs">
                                <single-select :options="genreList" v-model="musicInfo.genre" is-var>
                                </single-select>
                            </div>
                            <button class="btn btn-sm btn-ghost tooltip tooltip-top" data-tip="AI识别风格" @click="askGenre"
                                :disabled="isLoadingGenre">
                                <svg-icon v-if="!isLoadingGenre" name="ai" class="w-4 h-4 fill-primary" />
                                <span class="loading loading-ring loading-sm" v-else></span>
                            </button>
                        </div>

                        <!-- 语言 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'language'">
                            <label class="label-text font-medium min-w-20">语言</label>
                            <div class="flex-1 max-w-xs">
                                <single-select :options="languageList" v-model="musicInfo.language" is-var>
                                </single-select>
                            </div>
                        </div>

                        <!-- 标签 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'label'">
                            <label class="label-text font-medium min-w-20">标签</label>
                            <div class="flex-1">
                                <single-tag v-model="musicInfo.labels" :options="musicInfo.label_list"
                                    @change="handleLabelChange"></single-tag>
                            </div>
                        </div>

                        <!-- 专辑类型 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'album_type'">
                            <label class="label-text font-medium min-w-20">专辑类型</label>
                            <div class="flex-1 max-w-xs">
                                <single-select :options="albumTypeList" v-model="musicInfo.album_type" is-var>
                                </single-select>
                            </div>
                        </div>

                        <!-- 歌词 -->
                        <div v-if="item === 'lyrics'" class="space-y-2" :key="'lyrics_' + index">
                            <div class="space-y-3">
                                <div class="flex items-center justify-between">
                                    <label class="label-text font-medium">歌词</label>
                                    <!-- 移动到下方工具栏 -->
                                </div>

                                <!-- 歌词工具栏 - 独立行显示，更好的间距 -->
                                <div class="bg-base-200/30 rounded-lg p-3" v-if="mode !== 'form'">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <!-- 编辑工具组 -->
                                        <div class="flex items-center gap-2">
                                            <span class="text-xs font-medium text-base-content/60">编辑：</span>
                                            <div class="btn-group">
                                                <button class="btn btn-sm tooltip tooltip-top mr-2" data-tip="裁剪时间标记"
                                                    @click.stop="cutOneSecondToLyrics(musicInfo.lyrics)">
                                                    <svg class="w-4 h-4 fill-current" viewBox="0 0 1024 1024">
                                                        <path
                                                            d="M946 337.4c-2.3-8.6-11.1-13.7-19.7-11.3l-324.9 87.1 87-324.9c1.1-4.1 0.5-8.5-1.6-12.2s-5.6-6.4-9.7-7.5c-31-8.3-63.8-4.5-92.5 10.7-28 14.8-48.2 38.9-55.4 65.9l-74.8 279c-16.3-30.2-40.6-55.5-71.1-73.1-43.3-25-93.8-31.7-142.1-18.7l-82.7 22.2c-31.7 8.5-58.3 28.9-74.7 57.3-16.4 28.5-20.8 61.6-12.3 93.4L102 619c8.4 31.2 35.8 49.9 69.6 49.9 8.3 0 17.1-1.1 26-3.5l207.2-55.5-55.5 207.2c-12 44.9 7.5 85.1 46.4 95.5l113.7 30.5c10.6 2.8 21.4 4.2 32 4.2 21.3 0 42.4-5.6 61.3-16.5 28.5-16.4 48.8-43 57.3-74.7l22.2-82.7c22.9-85.3-17.3-172.9-91.9-213.2l279-74.8c57.8-15.6 93.7-84.7 76.7-148zM560.2 153.5c5-18.5 19.3-35.2 39.4-45.9 16.6-8.8 35.1-12.6 53.3-10.9l-87.3 325.9-83.5 22.4 78.1-291.5z m-18.7 421.4c82.6 22.1 131.8 107.4 109.7 190L629 847.6c-6.3 23.5-21.3 43.1-42.4 55.2-21 12.2-45.5 15.4-69 9.1L404 881.4c-21.6-5.8-31.2-28.4-23.7-56.2l60.3-225.1 98.2-26.3c0.8 0.5 1.7 0.9 2.7 1.1zM861 454.3l-671.7 180c-27.8 7.4-50.4-2.1-56.2-23.7l-30.5-113.7c-6.3-23.5-3.1-48 9.1-69 12.1-21 31.8-36.1 55.2-42.4l82.7-22.2c13.4-3.6 26.9-5.3 40.4-5.3 26.9 0 53.4 7 77.3 20.8 35.9 20.7 61.6 54.2 72.3 94.2 1.1 4.1 3.8 7.6 7.5 9.7s8.1 2.7 12.2 1.6l458.6-122.9c4 44.4-24.2 84.1-56.9 92.9z">
                                                        </path>
                                                    </svg>
                                                </button>
                                                <button class="btn btn-sm tooltip tooltip-top mr-2" data-tip="格式化时间"
                                                    @click.stop="formatSecondToLyrics(musicInfo.lyrics)">
                                                    <svg-icon name="fomat-lyrics" class="w-4 h-4 fill-current" />
                                                </button>
                                                <button class="btn btn-sm tooltip tooltip-top mr-2" data-tip="提取作曲作词"
                                                    @click.stop="extractToLyrics(musicInfo.lyrics)">
                                                    <svg-icon name="parse-zuoqu" class="w-4 h-4 fill-current" />
                                                </button>
                                            </div>
                                        </div>

                                        <!-- 翻译工具组 -->
                                        <div class="flex items-center gap-2">
                                            <span class="text-xs font-medium text-base-content/60">翻译：</span>
                                            <div class="btn-group">
                                                <button class="btn btn-sm btn-ghost tooltip tooltip-top mr-2"
                                                    data-tip="AI翻译歌词" @click.stop="askLyrics"
                                                    :disabled="isLoadingLyrics">
                                                    <svg-icon v-if="!isLoadingLyrics" name="ai"
                                                        class="w-4 h-4 fill-primary" />
                                                    <span class="loading loading-ring loading-sm" v-else></span>
                                                </button>
                                                <button class="btn btn-sm tooltip tooltip-top" data-tip="机器翻译"
                                                    @click.stop="translation()">
                                                    <svg-icon name="transform" class="w-4 h-4 fill-current" />
                                                </button>
                                            </div>
                                        </div>

                                        <!-- 时间轴工具组 -->
                                        <div class="flex items-center gap-2">
                                            <span class="text-xs font-medium text-base-content/60">时间轴：</span>
                                            <div class="btn-group">
                                                <button class="btn btn-sm tooltip tooltip-top mr-2" data-tip="时间轴编辑器"
                                                    @click.stop="showLyricsTimeline">
                                                    <svg-icon name="lyric-timeline" class="w-4 h-4 fill-current" />
                                                </button>
                                                <button class="btn btn-sm tooltip tooltip-top mr-2" data-tip="时间+0.1秒"
                                                    @click.stop="addOneSecondToLyrics(musicInfo.lyrics, 'add')">
                                                    <svg-icon name="add" class="w-4 h-4 fill-current" />
                                                </button>
                                                <button class="btn btn-sm tooltip tooltip-top" data-tip="时间-0.1秒"
                                                    @click.stop="addOneSecondToLyrics(musicInfo.lyrics, 'sub')">
                                                    <svg-icon name="subtract" class="w-4 h-4 fill-current" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <textarea placeholder="请填入歌词" v-model="musicInfo.lyrics"
                                class="textarea textarea-bordered textarea-sm w-full h-48">
                        </textarea>

                            <div class="flex items-center gap-6">
                                <label class="label cursor-pointer justify-start space-x-3 py-2">
                                    <input type="checkbox" class="toggle toggle-xs toggle-primary"
                                        v-model="musicInfo.is_save_lyrics_file" />
                                    <div class="flex-1">
                                        <span class="label-text font-medium">导出 lrc</span>
                                        <div class="text-xs text-base-content/60 mt-1">将元数据中的歌词导出为 .lrc 文件</div>
                                    </div>
                                </label>
                                <label class="label cursor-pointer justify-start space-x-3 py-2">
                                    <input type="checkbox" class="toggle toggle-xs toggle-primary"
                                        v-model="musicInfo.is_import_lyrics_file" />
                                    <div class="flex-1">
                                        <span class="label-text font-medium">导入 lrc</span>
                                        <div class="text-xs text-base-content/60 mt-1">将 .lrc 文件导入元数据中</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <!-- 歌词文件 -->
                        <div class="space-y-2" v-else-if="item === 'lyrics_file'">
                            <label class="label-text font-medium">歌词文件</label>
                            <textarea placeholder="同名的LRC歌词文件" v-model="musicInfo.lyrics_file"
                                class="textarea textarea-bordered textarea-sm w-full h-48">
                            </textarea>
                        </div>

                        <!-- 简介 -->
                        <div class="space-y-2" v-else-if="item === 'comment'">
                            <label class="label-text font-medium">简介</label>
                            <textarea placeholder="请填入简介" v-model="musicInfo.comment"
                                class="textarea textarea-bordered textarea-sm w-full">
                            </textarea>
                        </div>

                        <!-- 描述 -->
                        <div class="space-y-2" v-else-if="item === 'description'">
                            <label class="label-text font-medium">描述</label>
                            <textarea placeholder="请填入描述" v-model="musicInfo.description"
                                class="textarea textarea-bordered textarea-sm w-full">
                            </textarea>
                        </div>

                        <!-- 作曲家 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'composer'">
                            <label class="label-text font-medium min-w-20">作曲家</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.composer" :music-data="musicInfo" field-name="composer"></single-input>
                            </div>
                        </div>

                        <!-- 作词家 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'lyricist'">
                            <label class="label-text font-medium min-w-20">作词家</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.lyricist" :music-data="musicInfo" field-name="lyricist"></single-input>
                            </div>
                        </div>

                        <!-- 专辑封面 -->
                        <div v-if="item === 'album_img'" class="space-y-3" :key="'media_' + index">
                            <label class="label-text font-medium">专辑封面</label>
                            <div v-if="reloadImg">
                                <div class="relative">
                                    <!-- 图片预览区域 -->
                                    <div
                                        class="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center hover:border-gray-400 transition-colors">
                                        <div v-if="albumImagePreview" class="relative inline-block">
                                            <img :src="albumImagePreview" alt="专辑封面"
                                                class="max-w-full max-h-48 rounded-lg shadow-md">
                                            <button @click.stop="removeAlbumImage"
                                                class="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error text-white hover:bg-red-600 z-50">
                                                ✕
                                            </button>
                                            <!-- 替换图片按钮 -->
                                            <button @click.stop="triggerAlbumImageReplacement"
                                                class="absolute -bottom-2 -right-2 btn btn-circle btn-xs btn-neutral text-white z-50"
                                                title="替换图片">
                                                +
                                            </button>
                                        </div>
                                        <div v-else>
                                            <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor"
                                                fill="none" viewBox="0 0 48 48">
                                                <path
                                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                            <p class="mt-2 text-sm text-gray-500">点击上传或拖拽图片到此处</p>
                                            <p class="text-xs text-gray-400">支持 JPG, PNG, GIF (最大 5MB)</p>
                                        </div>

                                        <!-- 隐藏的文件输入 -->
                                        <input ref="albumImageInput" type="file" accept="image/*"
                                            @change="handleAlbumImageChange"
                                            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            :disabled="isUploadingAlbumImage" v-show="!albumImagePreview">
                                        <!-- 上传进度 -->
                                        <div v-if="isUploadingAlbumImage"
                                            class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                            <div class="text-center">
                                                <span class="loading loading-spinner loading-md"></span>
                                                <p class="mt-2 text-sm text-gray-600">上传中...</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="text-xs text-base-content/60 flex gap-4">
                                    <div>({{ musicInfo.artwork_w }}*{{ musicInfo.artwork_h }})</div>
                                    <div>{{ musicInfo.artwork_size }}MB</div>
                                </div>
                                <div class="flex items-center gap-6">
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-xs toggle-primary"
                                            v-model="musicInfo.is_save_album_cover" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">导出图片</span>
                                            <div class="text-xs text-base-content/60 mt-1">将专辑封面导出为单独图片文件</div>
                                        </div>
                                    </label>
                                    <label class="label cursor-pointer justify-start space-x-3 py-2">
                                        <input type="checkbox" class="toggle toggle-xs toggle-primary"
                                            v-model="musicInfo.is_import_album_cover" />
                                        <div class="flex-1">
                                            <span class="label-text font-medium">导入图片</span>
                                            <div class="text-xs text-base-content/60 mt-1">从同目录图片文件导入封面</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- 试听 -->
                        <div class="space-y-2" v-else-if="item === 'play'">
                            <!-- <label class="label-text font-medium">试听</label> -->
                            <audio controls muted class="w-full" :src="playPath"></audio>
                        </div>

                        <!-- 光盘编号 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'discnumber'">
                            <label class="label-text font-medium min-w-20">光盘编号</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.discnumber" :music-data="musicInfo" field-name="discnumber"></single-input>
                            </div>
                        </div>

                        <!-- 音轨号 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'tracknumber'">
                            <label class="label-text font-medium min-w-20">音轨号</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.tracknumber" :music-data="musicInfo" field-name="tracknumber"></single-input>
                            </div>
                        </div>

                        <!-- 总音轨数 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'totaltracks'">
                            <label class="label-text font-medium min-w-20">总音轨数</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.totaltracks" :music-data="musicInfo" field-name="totaltracks"></single-input>
                            </div>
                        </div>

                        <!-- 总光盘数 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'totaldiscs'">
                            <label class="label-text font-medium min-w-20">总光盘数</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.totaldiscs" :music-data="musicInfo" field-name="totaldiscs"></single-input>
                            </div>
                        </div>

                        <!-- 时长 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'duration'">
                            <label class="label-text font-medium min-w-20">时长</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.duration }}</div>
                        </div>

                        <!-- 比特率 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'bit_rate'">
                            <label class="label-text font-medium min-w-20">比特率</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.bit_rate }}</div>
                        </div>

                        <!-- 采样率 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'sample_rate'">
                            <label class="label-text font-medium min-w-20">采样率</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.sample_rate }}</div>
                        </div>

                        <!-- 位深度 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'bit_depth'">
                            <label class="label-text font-medium min-w-20">位深度</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.bit_depth }}</div>
                        </div>

                        <!-- 声道数 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'channels'">
                            <label class="label-text font-medium min-w-20">声道数</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.channels }}</div>
                        </div>

                        <!-- 文件大小 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'size'">
                            <label class="label-text font-medium min-w-20">文件大小</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.size }} MB</div>
                        </div>

                        <!-- 曲目增益 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'replaygain_track_gain'">
                            <label class="label-text font-medium min-w-20">曲目增益</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.replaygain_track_gain" :music-data="musicInfo" field-name="replaygain_track_gain"></single-input>
                            </div>
                        </div>

                        <!-- 曲目峰值 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'replaygain_track_peak'">
                            <label class="label-text font-medium min-w-20">曲目峰值</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.replaygain_track_peak" :music-data="musicInfo" field-name="replaygain_track_peak"></single-input>
                            </div>
                        </div>

                        <!-- 专辑增益 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'replaygain_album_gain'">
                            <label class="label-text font-medium min-w-20">专辑增益</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.replaygain_album_gain" :music-data="musicInfo" field-name="replaygain_album_gain"></single-input>
                            </div>
                        </div>

                        <!-- 专辑峰值 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'replaygain_album_peak'">
                            <label class="label-text font-medium min-w-20">专辑峰值</label>
                            <div class="flex-1 max-w-xs">
                                <single-input v-model="musicInfo.replaygain_album_peak" :music-data="musicInfo" field-name="replaygain_album_peak"></single-input>
                            </div>
                        </div>
                        <!-- 元数据版本 -->
                        <div class="flex items-center space-x-3" v-else-if="item === 'id3_version'">
                            <label class="label-text font-medium min-w-20">元数据版本</label>
                            <div class="badge badge-ghost text-xs">{{ musicInfo.id3_version }}</div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SingleSelect from "@/components/iview/SingleSelect";
import MultiSelect from "@/components/iview/MultiSelect";
import SingleInput from "@/components/iview/SingleInput";
import SingleTag from '@/components/iview/SingleTag.vue';
import SvgIcon from '@/components/SvgIcon.vue'
import ScrapingSettingsContent from '@/components/home/ScrapingSettingsContent.vue'

export default {
    name: 'music-meta-data',
    components: {
        SingleSelect,
        MultiSelect,
        SingleInput,
        SvgIcon,
        SingleTag,
        ScrapingSettingsContent
    },
    props: {
        musicInfo: { type: Object, required: true },
        reloadImg: { type: Boolean, required: true },
        loading: { type: Boolean, required: false },
        drawerMode: { type: String, required: true },
        mode: { type: String, required: false, default: 'edit' }
    },
    data() {
        return {
            isLightTheme: false,
            exampleSetting3: {
                primary: {
                    visible: false,
                    headerPosition: 'left'
                }
            },
            // 使用���的配置系统
            resource: 'netease',
            showFields: ['filename', 'artist', 'album', 'albumartist', 'genre', 'year', 'lyrics', 'lyrics_file', 'comment', 'album_img', 'size', 'album_type', 'description', 'discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'bit_rate', 'sample_rate', 'channels', 'bit_depth', 'language', 'duration', 'id3_version'],
            // 新增配置数据
            fieldGroups: [],
            albumTypeList: [
                { id: 'album;album', name: '普通(album;album)' },
                { id: 'ep', name: 'EP(ep)' },
                { id: 'album;compilation', name: '合集(album;compilation)' },
                { id: 'album;live', name: '现场(album;live)' },
                { id: 'album;remix', name: '混音(album;remix)' },
                { id: 'album;soundtrack', name: '原声(album;soundtrack)' },
                { id: 'album;demo', name: '演示(album;demo)' },
                { id: 'single', name: '单曲(single)' }
            ],
            files2: [],
            uploadUrl: '/apimt/upload_image/',
            uploadHeader: [
                { name: 'X-CSRFToken', value: this.getCookie('django_vue_cli_csrftoken') },
                { name: 'AUTHORIZATION', value: this.getCookie('AUTHORIZATION') }
            ],
            // 新增的图片上传相关数据
            albumImagePreview: null,
            isUploadingAlbumImage: false,
            genreList: [
                { 'id': '流行', name: '流行' },
                { 'id': '摇滚', name: '摇滚' },
                { 'id': '说唱', name: '说唱' },
                { 'id': '民谣', name: '民谣' },
                { 'id': '电子', name: '电子' },
                { 'id': '爵士', name: '爵士' },
                { 'id': '纯音乐', name: '纯音乐' },
                { 'id': '金属', name: '金属' },
                { 'id': '古典', name: '古典' },
                { 'id': '独立', name: '独立' },
            ],
            languageList: [
                { 'id': '中文', name: '中文' },
                { 'id': '英文', name: '英文' },
                { 'id': '日文', name: '日文' },
                { 'id': '韩文', name: '韩文' },
                { 'id': '法文', name: '法文' },
                { 'id': '泰文', name: '泰文' },
                { 'id': '未知', name: '未知' },
                { 'id': '${guess_language}', name: '自动识别' },
            ],
            fieldList: [
                { id: 'filename', name: '文件名' },
                { id: 'artist', name: '艺术家' },
                { id: 'album', name: '专辑' },
                { id: 'album_type', name: '专辑类型' },
                { id: 'albumartist', name: '专辑艺术家' },
                { id: 'discnumber', name: '光盘编号' },
                { id: 'tracknumber', name: '音轨号' },
                { id: 'totaltracks', name: '总音轨数' },
                { id: 'totaldiscs', name: '总光盘数' },
                { id: 'genre', name: '风格' },
                { id: 'year', name: '年份' },
                { id: 'lyrics', name: '歌词' },
                { id: 'lyrics_file', name: '歌词(LRC文件)' },
                { id: 'comment', name: '简介' },
                { id: 'description', name: '描述' },
                { id: 'album_img', name: '专辑封面' },
                { id: 'duration', name: '时长' },
                { id: 'size', name: '文件大小' },
                { id: 'bit_rate', name: '比特率' },
                { id: 'sample_rate', name: '采样率' },
                { id: 'channels', name: '声道' },
                { id: 'bit_depth', name: '位深度' },
                { id: 'language', name: '语言' },
                { id: 'play', name: '试听' },
                { id: 'composer', name: '作曲家' },
                { id: 'lyricist', name: '作词家' },
                { id: 'replaygain_track_gain', name: '曲目增益' },
                { id: 'replaygain_track_peak', name: '曲目峰值' },
                { id: 'replaygain_album_gain', name: '专辑增益' },
                { id: 'replaygain_album_peak', name: '专辑峰值' },
                { id: 'label', name: '标签' },
                { id: 'id3_version', name: '元数据版本' }
            ],
            resourceList: [],
            albumArtistInputList: '',
            isLoadingGenre: false,
            isLoadingLyrics: false,
            searchKeywordList: [
                { id: '${filename}', name: '${filename}' },
                { id: '${artist} - ${title}', name: '${artist} - ${title}' },
            ],
            searchKeyword: '${artist} - ${title}',
            // 专辑封面搜索相关
            albumSearchKeyword: '',
            albumSearchResults: [],
            isSearchingAlbumImage: false
        }
    },
    computed: {
        ...mapGetters(['geFullPath']),
        isChecked: {
            get() {
                return this.$store.state.common.checkedIds
            }
        },
        albumArtistList: {
            get() {
                return this.parseAlbumArtistList(this.musicInfo.artist)
            }
        },
        files1: {
            get() {
                if (this.musicInfo.artwork) {
                    return [
                        {
                            name: 'cover.png',
                            status: 'done',
                            url: this.musicInfo.artwork
                        }
                    ]
                } else {
                    return [
                        {
                            name: 'cover.png',
                            status: 'done',
                            url: this.musicInfo.album_img
                        }
                    ]
                }
            }
        },
        filePath: {
            get() {
                return this.geFullPath
            }
        },
        playPath: {
            get() {
                if (this.musicInfo.path) {
                    return this.musicInfo.path.replace("/app/", "/")
                } else {
                    return ''
                }
            }
        }
    },
    methods: {
        // 检查当前主题
        checkTheme() {
            const htmlElement = document.documentElement
            const theme = htmlElement.getAttribute('data-theme')
            this.isLightTheme = theme === 'light'
        },
        // 监听主题变化
        observeThemeChange() {
            const htmlElement = document.documentElement
            this.themeObserver = new MutationObserver(() => {
                this.checkTheme()
            })
            this.themeObserver.observe(htmlElement, {
                attributes: true,
                attributeFilter: ['data-theme']
            })
        },
        openSettingsModal() {
            this.$refs.settingsModal.showModal()
        },
        closeSettingsModal() {
            this.$refs.settingsModal.close()
        },
        // 字段分组方法
        getGroupFields(groupName) {
            // 优先使用动态配置的分组
            if (this.fieldGroups && this.fieldGroups.length > 0) {
                const group = this.fieldGroups.find(g => g.id === groupName)
                if (group) {
                    // 直接返回分组中的字段，不要通过 showFields 过滤
                    return group.fields || []
                }
            }

            // 降级到默认配置
            const defaultFieldGroups = {
                basic: ['filename', 'artist', 'album', 'albumartist', 'year'],
                category: ['genre', 'language', 'label', 'album_type'],
                lyrics: ['lyrics', 'lyrics_file'],
                metadata: ['comment', 'description', 'composer', 'lyricist'],
                media: ['album_img', 'play'],
                technical: ['discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'duration', 'bit_rate', 'sample_rate', 'bit_depth', 'channels', 'size', 'id3_version'],
                audio: ['replaygain_track_gain', 'replaygain_track_peak', 'replaygain_album_gain', 'replaygain_album_peak']
            }
            return defaultFieldGroups[groupName] || []
        },
        hasGroupFields(groupName) {
            return this.getGroupFields(groupName).length > 0
        },
        // 获取分组显示信息
        getGroupInfo(groupName) {
            const groupInfoMap = {
                basic: {
                    title: '基本信息',
                    badge: '核心字段',
                    badgeClass: 'badge-primary badge-outline',
                    barClass: 'bg-primary/40'
                },
                category: {
                    title: '分类信息',
                    badge: '分类标签',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                },
                lyrics: {
                    title: '歌词信息',
                    badge: '内容编辑',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                },
                media: {
                    title: '媒体信息',
                    badge: '媒体文件',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                },
                metadata: {
                    title: '元数据信息',
                    badge: '附加信息',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                },
                technical: {
                    title: '技术信息',
                    badge: '文件属性',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                },
                audio: {
                    title: '音频增益',
                    badge: 'ReplayGain',
                    badgeClass: 'badge-ghost',
                    barClass: 'bg-base-content/20'
                }
            }
            return groupInfoMap[groupName] || {
                title: groupName,
                badge: '',
                badgeClass: 'badge-ghost',
                barClass: 'bg-base-content/20'
            }
        },
        // 单个音乐保存信息
        handleClick() {
            const musicInfoCopy = JSON.parse(JSON.stringify(this.musicInfo))
            const updatedDictionary = Object.fromEntries(
                Object.entries(musicInfoCopy).map(([key, value]) => [key, value || '${null}'])
            )
            if (updatedDictionary.files1 && updatedDictionary.files1[0].name === 'new.png') {
                updatedDictionary.album_img = updatedDictionary.files1[0].url
            }
            const params = [{
                'file_full_path': this.musicInfo.path ? this.musicInfo.path : this.filePath + '/' + this.fileName,
                ...updatedDictionary
            }]
            this.isLoading = true
            this.$api.Task.updateId3({ 'music_id3_info': params }).then((res) => {
                this.isLoading = false
                if (res.result) {
                    this.$myMsg.notify({
                        title: '修改成功！',
                        type: 'success',
                        time: 2000,
                    })                    // 更新消息中心
                    // this.$store.commit('setHasMsg', true)
                    // 刷新目录
                    this.$parent.handleClick()
                } else {
                    this.$myMsg.notify({
                        title: '修改失败！',
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        // 渲染图片
        handleRes(response) {
            if (response.result) {
                this.musicInfo.album_img = response.data
                return true
            } else {
                return false
            }
        },
        toggleLock(mode) {
            this.$parent.toggleLock(this.drawerMode)
        },
        cutOneSecondToLyrics(lyrics) {
            // 使用正则表达式匹配歌词中的时间标记
            const timePattern = /\[(\d{2}):(\d{2}).(\d{2,3})\]/g

            // 使用replace()方法替换匹配到的时间标记
            const newLyrics = lyrics.replace(timePattern, function (match, minutes, seconds, milliseconds) {
                return ''
            })
            this.musicInfo.lyrics = newLyrics
            return newLyrics
        },
        formatSecondToLyrics(lyrics) {
            // 使用正则表达式匹配歌词中的时间标记
            const timePattern = /\[(\d{2}):(\d{2}).(\d{3})\]/g

            // 使用replace()方法替换匹配到的时间标记
            const newLyrics = lyrics.replace(timePattern, function (match, minutes, seconds, milliseconds) {
                // 返回新的时间标记
                return '[' + minutes + ':' + seconds + '.' + milliseconds.slice(0, 2) + ']'
            })
            this.musicInfo.lyrics = newLyrics
            return newLyrics
        },
        addOneSecondToLyrics(lyrics, mode) {
            // 使用正则表达式匹配歌词中的时间标记
            const timePattern = /\[(\d{2}):(\d{2})\.(\d{2,3})\]/g;

            // 使用replace()方法替换匹配到的时间标记
            const newLyrics = lyrics.replace(timePattern, function (match, minutes, seconds, milliseconds) {
                let newMilliseconds;
                // 将毫秒转换为整数
                const millisecondsLength = milliseconds.length
                if (millisecondsLength === 3) {
                    milliseconds = milliseconds.slice(0, 2)
                }
                let totalMilliseconds = parseInt(milliseconds, 10);
                let totalSeconds = parseInt(seconds, 10)
                // 增加或减少0.1秒等于100毫秒
                if (mode === 'add') {
                    totalMilliseconds += 10;
                    // 处理毫秒溢出
                    newMilliseconds = totalMilliseconds % 100;
                    totalSeconds = parseInt((totalMilliseconds - newMilliseconds) / 100, 10) + totalSeconds
                } else {
                    totalMilliseconds -= 10;
                    if (totalMilliseconds < 0) {
                        newMilliseconds = totalMilliseconds + 100;
                        totalSeconds = totalSeconds - 1
                    } else {
                        newMilliseconds = totalMilliseconds;
                    }
                }

                // 处理秒数溢出
                let newSeconds = totalSeconds % 60;
                let newMinutes = Math.floor(totalSeconds / 60) + parseInt(minutes, 10);

                // 格式化为两位数
                newMinutes = ('0' + newMinutes).slice(-2);
                newSeconds = ('0' + newSeconds).slice(-2);
                newMilliseconds = ('00' + newMilliseconds).slice(-2); // 确保毫秒是三字符长
                // 返回新的时间标记
                return '[' + newMinutes + ':' + newSeconds + '.' + newMilliseconds + ']';
            });

            this.musicInfo.lyrics = newLyrics;
            return newLyrics;
        },
        translation() {
            this.$parent.translation()
        },
        // 处理专辑艺术家
        parseAlbumArtistList(artists) {
            let ret = []
            if (artists) {
                let artistArray = []
                if (artists.indexOf(',') > -1) {
                    artistArray = artists.split(',')
                } else if (artists.indexOf('/') > -1) {
                    artistArray = artists.split('/')
                } else if (artists.indexOf('&') > -1) {
                    artistArray = artists.split('&')
                } else if (artists.indexOf('、') > -1) {
                    artistArray = artists.split('、')
                } else if (artists.indexOf('，') > -1) {
                    artistArray = artists.split('，')
                } else {
                    artistArray = []
                }
                artistArray.push(artists)
                ret = artistArray.map((item) => {
                    return {
                        'id': item,
                        'name': item
                    }
                })
            }
            return ret
        },
        // 处理配置更新
        handleConfigUpdated(config) {

            this.resource = config.resource || 'netease'
            this.fieldGroups = [...(config.fieldGroups || [])]


            // 从分组中生成显示字段列表
            this.generateShowFieldsFromGroups()


            // 保存到本地存储以保持兼容性
            localStorage.setItem('resource', this.resource)
            localStorage.setItem('showFields', JSON.stringify(this.showFields))

            // 强制重新渲染
            this.$forceUpdate()
        },
        // 从分组中生成显示字段列表
        generateShowFieldsFromGroups() {
            const allFields = []
            this.fieldGroups.forEach(group => {
                if (group.fields && Array.isArray(group.fields)) {
                    allFields.push(...group.fields)
                }
            })
            // 去重并保持顺序
            this.showFields = [...new Set(allFields)]
        },

        // 初始化配置
        initializeConfig() {
            try {
                // 优先尝试加载新的配置格式
                const savedConfig = localStorage.getItem('scrapingSettings')
                if (savedConfig) {
                    const config = JSON.parse(savedConfig)
                    this.handleConfigUpdated(config)
                    return
                }

                // 如果没有新配置，尝试加载旧配置并转换
                const legacyResource = localStorage.getItem('resource')
                const legacyShowFields = localStorage.getItem('showFields')


                if (legacyResource) {
                    this.resource = legacyResource
                }
                if (legacyShowFields) {
                    this.showFields = JSON.parse(legacyShowFields)
                    // 从旧的 showFields 反向生成默认分组
                    this.generateDefaultGroupsFromFields()
                } else {
                    // 如果没有任何配置，使用默认值
                    this.resource = 'netease'
                    this.showFields = ['filename', 'artist', 'album', 'albumartist', 'genre', 'year', 'lyrics', 'lyrics_file', 'comment', 'album_img', 'size', 'album_type', 'description', 'discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'bit_rate', 'sample_rate', 'channels', 'bit_depth', 'language', 'duration']
                    this.generateDefaultGroupsFromFields()
                }
            } catch (error) {
                console.error('初始化配置失败:', error)
                // 发生错误时使用默认配置
                this.resource = 'netease'
                this.showFields = ['filename', 'artist', 'album', 'albumartist', 'genre', 'year', 'lyrics', 'lyrics_file', 'comment', 'album_img', 'size', 'album_type', 'description', 'discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'bit_rate', 'sample_rate', 'channels', 'bit_depth', 'language', 'duration']
                this.generateDefaultGroupsFromFields()
            }
        },

        // 从旧的 showFields 生成默认分组（兼容性处理）
        generateDefaultGroupsFromFields() {
            const defaultFieldGroups = [
                {
                    id: 'basic',
                    name: '基础信息',
                    fields: ['filename', 'artist', 'album', 'albumartist', 'year'],
                    isCustom: false
                },
                {
                    id: 'media',
                    name: '媒体文件',
                    fields: ['album_img', 'play'],
                    isCustom: false
                },
                {
                    id: 'lyrics',
                    name: '歌词相关',
                    fields: ['lyrics', 'lyrics_file'],
                    isCustom: false
                },
                {
                    id: 'category',
                    name: '分类信息',
                    fields: ['genre', 'language', 'label', 'album_type'],
                    isCustom: false
                },
                {
                    id: 'metadata',
                    name: '元数据',
                    fields: ['comment', 'description', 'composer', 'lyricist'],
                    isCustom: false
                },
                
                {
                    id: 'technical',
                    name: '技术参数',
                    fields: ['discnumber', 'tracknumber', 'totaltracks', 'totaldiscs', 'duration', 'bit_rate', 'sample_rate', 'bit_depth', 'channels', 'size'],
                    isCustom: false
                },
                {
                    id: 'audio',
                    name: '音频增益',
                    fields: ['replaygain_track_gain', 'replaygain_track_peak', 'replaygain_album_gain', 'replaygain_album_peak'],
                    isCustom: false
                }
            ]

            // 将字段分配到对应的分组中
            this.fieldGroups = defaultFieldGroups.map(group => ({
                ...group,
                fields: group.fields.filter(field => this.showFields.includes(field))
            }))
        },
        extractToLyrics(lyc) {
            this.$api.Task.extractLyric({ 'lyc': lyc }).then((res) => {
                if (res.result) {
                    if (res.data !== '') {
                        this.musicInfo['composer'] = res.data.composer
                        this.musicInfo['lyricist'] = res.data.lyricist
                    } else {
                        this.$myMsg.notify({
                            title: '未找到歌词！',
                            type: 'error',
                            time: 2000,
                        })
                    }
                } else {
                    console.log()
                }
            })
        },
        showSettings() {
            this.$refs['tag_settings'].showModal()
        },
        askGenre() {
            this.isLoadingGenre = true
            this.$api.agents.askGenre({ 'track_info': this.musicInfo }).then((res) => {
                this.isLoadingGenre = false
                if (res.result) {
                    this.musicInfo.genre = res.data
                    this.$myMsg.notify({
                        title: 'AI识别成功！',
                        content: res.data,
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.$myMsg.notify({
                        title: 'AI识别失败！',
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        askLyrics() {
            this.isLoadingLyrics = true
            this.$api.agents.askLyrics({ 'track_info': this.musicInfo }).then((res) => {
                this.isLoadingLyrics = false
                if (res.result) {
                    this.musicInfo.lyrics = res.data
                    this.$myMsg.notify({
                        title: 'AI翻译成功！',
                        content: res.data,
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.$myMsg.notify({
                        title: 'AI翻译失败！',
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        // 新增的图片上传相关方法
        // 处理专辑封面图片选择
        handleAlbumImageChange(event) {
            const file = event.target.files[0]
            if (!file) return

            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                this.$myMsg.notify({
                    title: '文件类型错误',
                    content: '请选择图片文件',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 验证文件大小 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: '文件过大',
                    content: '图片大小不能超过5MB',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 显示预览
            const reader = new FileReader()
            reader.onload = (e) => {
                this.albumImagePreview = e.target.result
            }
            reader.readAsDataURL(file)

            // 上传图片
            this.uploadImage(file, 'album')
        },


        // 通用图片上传方法
        uploadImage(file, type) {
            const formData = new FormData()
            formData.append('upload_file', file)

            // 设置上传状态
            if (type === 'album') {
                this.isUploadingAlbumImage = true
            } else {
                this.isUploadingArtistImage = true
            }

            // 创建 XMLHttpRequest
            const xhr = new XMLHttpRequest()

            // 设置请求头
            xhr.open('POST', this.uploadUrl, true)
            xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
            xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
            xhr.withCredentials = true

            // 处理响应
            xhr.onload = () => {
                // 重置上传状态
                if (type === 'album') {
                    this.isUploadingAlbumImage = false
                } else {
                    this.isUploadingArtistImage = false
                }

                if (xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText)
                        if (this.handleRes(response)) {
                            this.$myMsg.notify({
                                title: '上传成功',
                                type: 'success',
                                time: 2000,
                            })
                        } else {
                            this.$myMsg.notify({
                                title: '上传失败',
                                content: response.message || '未知错误',
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } catch (e) {
                        this.$myMsg.notify({
                            title: '上传失败',
                            content: '服务器响应格式错误',
                            type: 'error',
                            time: 2000,
                        })
                    }
                } else {
                    this.$myMsg.notify({
                        title: '上传失败',
                        content: `HTTP错误: ${xhr.status}`,
                        type: 'error',
                        time: 2000,
                    })
                }
            }

            // 处理错误
            xhr.onerror = () => {
                if (type === 'album') {
                    this.isUploadingAlbumImage = false
                } else {
                    this.isUploadingArtistImage = false
                }
                this.$myMsg.notify({
                    title: '上传失败',
                    content: '网络错误',
                    type: 'error',
                    time: 2000,
                })
            }

            // 发送请求
            xhr.send(formData)
        },

        // 移除专辑封面
        removeAlbumImage() {
            this.albumImagePreview = null
            this.musicInfo.album_img = ''
            this.musicInfo.artwork = ''
            if (this.$refs.albumImageInput) {
                this.$refs.albumImageInput.value = ''
            }
        },


        // 初始化图片预览
        initImagePreview() {
            // 初始化专辑封面预览
            if (this.musicInfo.artwork) {
                this.albumImagePreview = this.musicInfo.artwork
            } else if (this.musicInfo.album_img) {
                this.albumImagePreview = this.musicInfo.album_img
            } else {
                this.albumImagePreview = null
            }
        },

        // 触发专辑封面替换
        triggerAlbumImageReplacement() {
            // 直接创建临时input，更可靠
            this.createTempFileInput('album')
        },

        // 创建临时文件输入
        createTempFileInput(type) {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.style.display = 'none'
            input.onchange = (event) => {
                if (type === 'album') {
                    this.handleAlbumImageChange(event)
                } else {
                    this.handleArtistImageChange(event)
                }
                // 清理临时元素
                if (input.parentNode) {
                    input.parentNode.removeChild(input)
                }
            }
            document.body.appendChild(input)
            input.click()
        },
        handleArtistChange(value) {
            this.musicInfo.artist = value.join(',')
        },
        handleLabelChange(value) {
            this.musicInfo.label = value.join(',')
        },
        showLyricsTimeline() {
            let playPath = this.playPath
            this.$parent.showLyricsTimelineModal(playPath, this.musicInfo.lyrics)
        },
        // 从 Vuex store 加载标签源列表
        async loadTagSourcesFromStore() {
            try {
                // 从 Vuex store 加载标签源（带缓存）
                const sources = await this.$store.dispatch('loadTagSources')
                this.resourceList = sources

                // 检查当前选中的资源是否还存在于新的列表中
                const availableResourceIds = this.resourceList.map(item => item.id)
                if (!availableResourceIds.includes(this.resource)) {
                    // 如果当前选中的资源不存在,设置为列表中的第一个
                    if (this.resourceList.length > 0) {
                        this.resource = this.resourceList[0].id
                        localStorage.setItem('resource', this.resource)
                    }
                }
            } catch (error) {
                console.error('加载标签源失败:', error)
            }
        },

        // 显示版本历史 - 通知父组件打开抽屉
        showVersionHistory() {
            if (!this.musicInfo || !this.musicInfo.path) {
                this.$myMsg.notify({
                    title: '无法查看版本历史',
                    content: '当前没有选中的音轨',
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            // 触发事件通知父组件
            this.$emit('show-version-history', {
                id: this.musicInfo.id,
                title: this.musicInfo.title || '未命名',
                path: this.musicInfo.path
            })
        },

        // 处理专辑封面文件上传
        handleFileUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                this.$myMsg.notify({
                    title: '文件类型错误',
                    content: '请选择图片文件',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 验证文件大小 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: '文件过大',
                    content: '图片大小不能超过5MB',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 显示预览
            const reader = new FileReader()
            reader.onload = (e) => {
                this.albumImagePreview = e.target.result
            }
            reader.readAsDataURL(file)

            // 上传图片
            this.uploadImage(file, 'album')
        },

        // 处理歌词文件上传
        handleLyricsFileUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            // 验证文件类型
            if (!file.name.endsWith('.lrc')) {
                this.$myMsg.notify({
                    title: '文件类型错误',
                    content: '请选择 .lrc 格式的歌词文件',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 验证文件大小 (1MB)
            if (file.size > 1 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: '文件过大',
                    content: '歌词文件大小不能超过1MB',
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 读取文件内容
            const reader = new FileReader()
            reader.onload = (e) => {
                const lyricsContent = e.target.result
                this.musicInfo.lyrics = lyricsContent
                this.$myMsg.notify({
                    title: '歌词文件加载成功',
                    type: 'success',
                    time: 2000,
                })
            }
            reader.onerror = () => {
                this.$myMsg.notify({
                    title: '读取文件失败',
                    content: '无法读取歌词文件内容',
                    type: 'error',
                    time: 2000,
                })
            }
            reader.readAsText(file, 'UTF-8')
        },

        // 搜索歌词
        searchLyric(source) {
            if (!this.musicInfo.title || !this.musicInfo.artist) {
                this.$myMsg.notify({
                    title: '搜索失败',
                    content: '请先填写歌曲标题和艺术家',
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            const params = {
                title: this.musicInfo.title,
                artist: this.musicInfo.artist,
                source: source
            }

            this.$api.Task.searchLyric(params).then((res) => {
                if (res.result && res.data) {
                    this.musicInfo.lyrics = res.data
                    this.$myMsg.notify({
                        title: '搜索成功',
                        content: `已从${source}获取歌词`,
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.$myMsg.notify({
                        title: '搜索失败',
                        content: res.message || '未找到歌词',
                        type: 'error',
                        time: 2000,
                    })
                }
            }).catch(() => {
                this.$myMsg.notify({
                    title: '搜索失败',
                    content: '网络错误或服务不可用',
                    type: 'error',
                    time: 2000,
                })
            })
        },

        // AI翻译歌词
        translateLyrics() {
            if (!this.musicInfo.lyrics) {
                this.$myMsg.notify({
                    title: '翻译失败',
                    content: '请先填写歌词内容',
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            this.isLoadingLyrics = true
            this.$api.agents.translateLyrics({
                lyrics: this.musicInfo.lyrics,
                track_info: this.musicInfo
            }).then((res) => {
                this.isLoadingLyrics = false
                if (res.result && res.data) {
                    this.musicInfo.lyrics = res.data
                    this.$myMsg.notify({
                        title: 'AI翻译成功',
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.$myMsg.notify({
                        title: 'AI翻译失败',
                        content: res.message || '翻译服务不可用',
                        type: 'error',
                        time: 2000,
                    })
                }
            }).catch(() => {
                this.isLoadingLyrics = false
                this.$myMsg.notify({
                    title: 'AI翻译失败',
                    content: '网络错误或服务不可用',
                    type: 'error',
                    time: 2000,
                })
            })
        },

        // 搜索专辑封面
        searchAlbumImage() {
            if (!this.albumSearchKeyword.trim()) {
                this.$myMsg.notify({
                    title: '搜索失败',
                    content: '请输入搜索关键词',
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            this.isSearchingAlbumImage = true
            this.$api.Task.searchAlbumImage({
                keyword: this.albumSearchKeyword.trim()
            }).then((res) => {
                this.isSearchingAlbumImage = false
                if (res.result && res.data && res.data.length > 0) {
                    this.albumSearchResults = res.data
                    this.$myMsg.notify({
                        title: '搜索成功',
                        content: `找到 ${res.data.length} 个结果`,
                        type: 'success',
                        time: 2000,
                    })
                } else {
                    this.albumSearchResults = []
                    this.$myMsg.notify({
                        title: '搜索失败',
                        content: '未找到相关图片',
                        type: 'warning',
                        time: 2000,
                    })
                }
            }).catch(() => {
                this.isSearchingAlbumImage = false
                this.albumSearchResults = []
                this.$myMsg.notify({
                    title: '搜索失败',
                    content: '网络错误或服务不可用',
                    type: 'error',
                    time: 2000,
                })
            })
        },

        // 选择专辑封面
        selectAlbumImage(imageUrl) {
            this.albumImagePreview = imageUrl
            this.musicInfo.album_img = imageUrl
            this.musicInfo.artwork = imageUrl
            this.$myMsg.notify({
                title: '封面已选择',
                type: 'success',
                time: 2000,
            })
        },
    },
    // 添加 watch 监听 musicInfo 变化
    watch: {
        musicInfo: {
            handler(newVal) {
                if (newVal) {
                    this.initImagePreview()
                }
            },
            deep: true,
            immediate: true
        }
    },
    // 添加 mounted 生命周期
    mounted() {
        this.initImagePreview()
        this.loadTagSourcesFromStore()
        this.initializeConfig()
        this.checkTheme()
        this.observeThemeChange()
    },
    beforeDestroy() {
        // 清理主题观察器
        if (this.themeObserver) {
            this.themeObserver.disconnect()
        }
    }
}
</script>

<style scoped>
/* 虚化背景效果 */
.blur-3xl {
    filter: blur(80px);
}

/* 背景图片平滑过渡 */
[style*="backgroundImage"] {
    transition: background-image 0.5s ease-in-out;
}

/* 分组悬停微动效果 */
.space-y-4:hover>div:first-child {
    @apply transform translate-x-1 transition-all duration-200;
}

/* 切换开关增强效果 */
.toggle:checked {
    @apply shadow-lg;
}

.toggle:focus {
    @apply ring-2 ring-primary/20 ring-offset-2 ring-offset-base-100;
}

/* 按钮悬停图标缩放 */
.btn:hover svg {
    @apply transform scale-110 transition-transform duration-200;
}

/* 表单控件悬停效果 */
.form-control:hover .label {
    @apply transform translate-x-1 transition-all duration-200;
}

/* 分组标识增强 */
.space-y-4 .w-1.h-5 {
    @apply transition-all duration-300;
}

.space-y-4:hover .w-1.h-5 {
    @apply h-6 shadow-md;
}

/* 徽章悬停效果 */
.badge:hover {
    @apply transform scale-105 transition-transform duration-200;
}

/* 上传区域增强 */
.border-dashed:hover {
    @apply border-primary/40 bg-primary/5 transition-all duration-300;
}

/* 按钮组样式 */
.btn-group .btn {
    @apply transition-all duration-200;
}

.btn-group:hover .btn {
    @apply shadow-sm;
}

/* 音频控件样式 */
audio {
    @apply rounded-lg;
}

audio::-webkit-media-controls-panel {
    @apply bg-base-200/30;
}

/* 响应式设计优化 */
@media (max-width: 768px) {

    /* 移动端间距调整 */
    .space-y-6 {
        @apply space-y-4;
    }

    .space-y-4 {
        @apply space-y-3;
    }

    /* 移动端文字大小调整 */
    .text-lg {
        @apply text-base;
    }

    /* 移动端按钮组优化 */
    .btn-group {
        @apply flex-wrap;
    }

    .btn-xs {
        @apply btn-sm;
    }

    /* 移动端标签控件优化 */
    .min-w-20 {
        @apply min-w-16 text-sm;
    }

    /* 移动端上传区域优化 */
    .max-h-48 {
        @apply max-h-32;
    }
}

/* 高对比度支持 */
@media (prefers-contrast: high) {
    .bg-primary\/40 {
        @apply bg-primary/60;
    }

    .text-base-content\/60 {
        @apply text-base-content/80;
    }
}

/* 动画偏好设置 */
@media (prefers-reduced-motion: reduce) {
    * {
        @apply transition-none;
    }
}
</style>